import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavScroll } from '@hooks/useNavScroll';
import { useAuth } from '@contexts/AuthContext';
import { useAuthGate } from '@contexts/AuthGateContext';
import { NAV_LINKS, NAV_ROUTE_LINKS, ROUTES } from '@constants/index';
import { scrollToAnchor, scrollToAnchorWhenReady } from '@utils/scroll';
import { mobileMenu, staggerFast, fadeUp } from '@animations/variants';
import Logo from '@components/navigation/Logo';

export default function Navbar() {
  const isScrolled = useNavScroll(20);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated, signOut } = useAuth();
  const { isGated, openPrompt } = useAuthGate();

  function handleNavClick(href: string) {
    setMobileOpen(false);
    // Every in-page section is behind the gate — take the visitor to it so they
    // can see what's there, then ask them to sign in.
    if (pathname === ROUTES.HOME) {
      scrollToAnchor(href);
    } else {
      // These sections only exist on the home page, so route there first and
      // scroll once the section has actually mounted.
      navigate(ROUTES.HOME);
      scrollToAnchorWhenReady(href);
    }
    if (isGated) openPrompt();
  }

  function go(path: string) {
    setMobileOpen(false);
    navigate(path);
    // React Router keeps the scroll position across routes; a new page should
    // start at its top rather than partway down.
    if (pathname !== path) window.scrollTo({ top: 0 });
  }

  async function handleSignOut() {
    setMobileOpen(false);
    await signOut();
    navigate('/');
  }

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300"
        style={{
          backgroundColor: 'rgba(10, 10, 10, 0.97)',
          borderBottom: `1px solid ${isScrolled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.06)'}`,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <div className="flex items-center justify-between px-8 h-[72px] max-w-[1400px] mx-auto">
          {/* Logo */}
          <Logo />

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10" aria-label="Main navigation">
            {NAV_LINKS.map(({ label, href }) => (
              <button
                key={href}
                onClick={() => handleNavClick(href)}
                className="flex items-center gap-2 font-sans text-label tracking-nav uppercase text-text-muted hover:text-text-primary transition-colors duration-200 bg-transparent border-none cursor-pointer"
              >
                {label}
                {isGated && <LockGlyph />}
              </button>
            ))}

            {/* Open to everyone, signed in or not — no lock, no gate prompt. */}
            {NAV_ROUTE_LINKS.map(({ label, to }) => (
              <button
                key={to}
                onClick={() => go(to)}
                aria-current={pathname === to ? 'page' : undefined}
                className={`font-sans text-label tracking-nav uppercase transition-colors duration-200 bg-transparent border-none cursor-pointer
                  ${pathname === to ? 'text-accent' : 'text-text-muted hover:text-text-primary'}
                `}
              >
                {label}
              </button>
            ))}
          </nav>

          {/* CTA + Auth + Hamburger */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <button
                onClick={handleSignOut}
                className="hidden md:inline-flex font-sans text-label tracking-nav uppercase text-text-muted hover:text-text-primary transition-colors duration-200"
              >
                Sign Out
              </button>
            ) : (
              <button
                onClick={() => go('/login')}
                className="hidden md:inline-flex font-sans text-label tracking-nav uppercase text-text-primary border border-accent/50 px-6 py-2.5 hover:border-accent hover:bg-accent/5 transition-all duration-200"
              >
                Sign In
              </button>
            )}

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden flex flex-col gap-[5px] p-2 bg-transparent border-none cursor-pointer"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              <motion.span
                animate={mobileOpen ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
                className="block w-[22px] h-px bg-text-primary origin-center"
                transition={{ duration: 0.2 }}
              />
              <motion.span
                animate={mobileOpen ? { opacity: 0 } : { opacity: 1 }}
                className="block w-[22px] h-px bg-text-primary"
                transition={{ duration: 0.2 }}
              />
              <motion.span
                animate={mobileOpen ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
                className="block w-[22px] h-px bg-text-primary origin-center"
                transition={{ duration: 0.2 }}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            variants={mobileMenu}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-[90] pt-[72px] bg-bg-primary md:hidden"
          >
            <motion.nav
              variants={staggerFast}
              initial="hidden"
              animate="visible"
              className="flex flex-col p-8 gap-8"
              aria-label="Mobile navigation"
            >
              {NAV_LINKS.map(({ label, href }) => (
                <motion.button
                  key={href}
                  variants={fadeUp}
                  onClick={() => handleNavClick(href)}
                  className="flex items-center gap-3 font-serif text-2xl font-light text-text-primary text-left bg-transparent border-none cursor-pointer hover:text-accent transition-colors duration-200"
                >
                  {label}
                  {isGated && <LockGlyph />}
                </motion.button>
              ))}

              {/* Open to everyone, signed in or not — no lock, no gate prompt. */}
              {NAV_ROUTE_LINKS.map(({ label, to }) => (
                <motion.button
                  key={to}
                  variants={fadeUp}
                  onClick={() => go(to)}
                  aria-current={pathname === to ? 'page' : undefined}
                  className={`font-serif text-2xl font-light text-left bg-transparent border-none cursor-pointer transition-colors duration-200
                    ${pathname === to ? 'text-accent' : 'text-text-primary hover:text-accent'}
                  `}
                >
                  {label}
                </motion.button>
              ))}

              {isAuthenticated ? (
                <motion.button
                  variants={fadeUp}
                  onClick={handleSignOut}
                  className="mt-4 font-sans text-label tracking-nav uppercase text-text-primary border border-border-strong px-8 py-4 self-start"
                >
                  Sign Out
                </motion.button>
              ) : (
                <motion.button
                  variants={fadeUp}
                  onClick={() => go('/login')}
                  className="mt-4 font-sans text-label tracking-nav uppercase text-bg-primary bg-accent px-8 py-4 self-start"
                >
                  Sign In
                </motion.button>
              )}
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/** Small padlock marking nav destinations that require a signed-in account. */
function LockGlyph() {
  return (
    <svg width="9" height="11" viewBox="0 0 12 14" fill="none" aria-hidden className="opacity-60">
      <path
        d="M3.5 6V3.75a2.5 2.5 0 0 1 5 0V6M2.5 6h7v6.5h-7V6Z"
        stroke="#FF6B00"
        strokeWidth="1.2"
        strokeLinecap="square"
      />
    </svg>
  );
}
